import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Récupère un endpoint authentifié et renvoie une URL blob (idéale pour <img src>).
// L'appelant est responsable de revoke quand l'image n'est plus affichée.
// type : force le type du blob (lecture video, cf. Thread.jsx).
export async function fetchAuthedBlobURL(path, type) {
  try {
    const res = await api.get(path, { responseType: 'blob' });
    return URL.createObjectURL(type ? new Blob([res.data], { type }) : res.data);
  } catch {
    return null;
  }
}

// Ouvre dans un nouvel onglet un endpoint authentifié (PDF, fichier).
// Astuce popup blocker : on PRÉ-ouvre la fenêtre AVANT l'await (donc encore
// dans le contexte synchrone du click utilisateur), puis on charge l'URL
// du blob dedans une fois la requête terminée.
// - Pas de téléchargement forcé sur le disque (sauf si filename fourni)
// - Pas de blocage popup (fenêtre ouverte sous l'event du click)
// - Le PDF s'affiche dans l'onglet du navigateur avec son lecteur intégré
export async function openAuthedFile(path, { filename } = {}) {
  // Mode "ouvrir dans onglet" si pas de filename
  let previewWin = null;
  if (!filename) {
    previewWin = window.open('about:blank', '_blank');
    if (previewWin && previewWin.document) {
      // Affiche un loader minimal via createElement (pas de document.write)
      const body = previewWin.document.body;
      if (body) {
        body.style.cssText = 'font-family:sans-serif;padding:40px;text-align:center;color:#666';
        const p = previewWin.document.createElement('p');
        p.textContent = 'Chargement du document…';
        body.appendChild(p);
        previewWin.document.title = 'Chargement…';
      }
    }
  }

  try {
    const res = await api.get(path, { responseType: 'blob' });

    // Si le serveur a renvoyé une erreur déguisée en blob
    if (res.data && res.data.type && res.data.type.includes('json')) {
      const text = await res.data.text();
      try {
        const err = JSON.parse(text);
        if (previewWin) previewWin.close();
        alert('Erreur serveur : ' + (err.message || 'inconnue'));
        return;
      } catch { /* pas du JSON */ }
    }

    const contentType = res.headers?.['content-type'] || 'application/octet-stream';
    if ((res.data?.size || 0) === 0) {
      if (previewWin) previewWin.close();
      alert('Le serveur a renvoyé un fichier vide.');
      return;
    }

    // Nom propose par le serveur (Content-Disposition). On construit un File
    // et non un Blob : certains navigateurs s'en servent comme nom par defaut
    // quand on enregistre depuis la visionneuse PDF, au lieu de l'identifiant
    // de l'URL blob — illisible, et introuvable ensuite dans les
    // telechargements.
    const disp = res.headers?.['content-disposition'] || '';
    const nomServeur = (disp.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/) || [])[1];
    const nomFichier = filename || (nomServeur ? decodeURIComponent(nomServeur) : null);
    const url = window.URL.createObjectURL(
      nomFichier
        ? new File([res.data], nomFichier, { type: contentType })
        : new Blob([res.data], { type: contentType })
    );

    if (filename) {
      // Mode download explicite
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } else if (previewWin) {
      // Charge le PDF dans la fenêtre pré-ouverte (pas de popup blocker)
      previewWin.location.href = url;
    } else {
      // Fallback si window.open a été bloqué : download forcé
      const disposition = res.headers?.['content-disposition'] || '';
      const dispMatch = disposition.match(/filename="?([^"]+)"?/);
      const autoName = dispMatch?.[1] || 'document.pdf';
      const a = document.createElement('a');
      a.href = url;
      a.download = autoName;
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  } catch (err) {
    if (previewWin) previewWin.close();
    console.error('openAuthedFile error', { path, err, response: err.response });
    let msg = err.response?.data?.message || err.message || 'erreur inconnue';
    if (err.response?.data instanceof Blob) {
      // Le serveur repond parfois en texte brut — les routes PDF renvoient
      // « Erreur generation PDF : <cause> ». Ne garder que le JSON laissait
      // « erreur inconnue » a l'ecran alors que la cause etait dans la
      // reponse, et obligeait a aller lire les journaux du serveur.
      const text = await err.response.data.text().catch(() => '');
      if (text) {
        try {
          msg = JSON.parse(text).message || text;
        } catch {
          msg = text.slice(0, 400);
        }
      }
    }
    alert('Erreur ouverture fichier : ' + msg);
  }
}

export default api;
